import {
    useMutationState,
    useQuery,
    useQueryClient,
    type QueryClient,
} from "@tanstack/react-query";
import { useToast } from "@jobenvoy/ui";
import { describeError } from "@/lib/api/client";
import {
    listSaved,
    markSaved,
    savePosting,
    unsavePosting,
    type SavedPosting,
    type SavedPostingList,
    type SavedStatus,
    type SkipReason,
} from "@/lib/api/envoy";
import { track } from "@/lib/analytics";
import { keys } from "./keys";
import { useSingleFlightMutation } from "./singleFlight";

const SAVE = ["envoy", "saved", "save"] as const;
const UNSAVE = ["envoy", "saved", "unsave"] as const;
const MARK = ["envoy", "saved", "mark"] as const;

/** A posting saved this moment, as the list shows it before the service answers. */
export function justSaved(postingId: string): SavedPosting {
    return {
        posting_id: postingId,
        saved_at: new Date().toISOString(),
        status: "SAVED",
        status_at: null,
        skip_reason: null,
        list_ids: [],
    };
}

/**
 * Changes the cached list at once, keeping the lists beside it, and
 * returns what was there to put back if the service refuses.
 */
export function changeSaved(
    client: QueryClient,
    change: (saved: SavedPostingList) => SavedPostingList,
): SavedPostingList | undefined {
    const before = client.getQueryData<SavedPostingList>(keys.envoy.saved);
    client.setQueryData<SavedPostingList>(
        keys.envoy.saved,
        change(before ?? { saved: [], lists: [] }),
    );
    return before;
}

/** The postings the person saved, the newest first. Asked only for someone signed in. */
export function useSaved(enabled = true) {
    return useQuery({ queryKey: keys.envoy.saved, queryFn: listSaved, enabled });
}

/** Which postings are saved, by id, for the Save button on every card. */
export function useSavedIds(enabled = true): ReadonlySet<string> {
    const saved = useSaved(enabled);
    return new Set((saved.data?.saved ?? []).map((entry) => entry.posting_id));
}

/**
 * Save and forget, each shown at once on the list and put back if the
 * service refuses. Saving costs nothing and tailors nothing.
 */
export function useSaveActions() {
    const client = useQueryClient();
    const toast = useToast();

    const setList = (change: (saved: SavedPosting[]) => SavedPosting[]) =>
        changeSaved(client, (list) => ({ ...list, saved: change(list.saved) }));
    const restore = (before: SavedPostingList | undefined) =>
        client.setQueryData(keys.envoy.saved, before);

    const save = useSingleFlightMutation(
        {
            mutationKey: SAVE,
            mutationFn: ({ postingId }: { postingId: string; title?: string }) =>
                savePosting(postingId),
            onMutate: ({ postingId }) => ({
                before: setList((saved) =>
                    saved.some((entry) => entry.posting_id === postingId)
                        ? saved
                        : [justSaved(postingId), ...saved],
                ),
            }),
            onSuccess: (_kept, { postingId, title }) => {
                track({ name: "posting_saved", posting_id: postingId });
                toast.success("Saved", title);
            },
            onError: (error, _variables, context) => {
                restore((context as { before?: SavedPostingList } | undefined)?.before);
                toast.error("Not saved", describeError(error));
            },
            onSettled: () => client.invalidateQueries({ queryKey: keys.envoy.saved }),
        },
        (variables) => variables.postingId,
    );

    const forget = useSingleFlightMutation(
        {
            mutationKey: UNSAVE,
            mutationFn: ({ postingId }: { postingId: string }) => unsavePosting(postingId),
            onMutate: ({ postingId }) => ({
                before: setList((saved) => saved.filter((entry) => entry.posting_id !== postingId)),
            }),
            onSuccess: (_answer, { postingId }) => {
                track({ name: "posting_unsaved", posting_id: postingId });
            },
            onError: (error, _variables, context) => {
                restore((context as { before?: SavedPostingList } | undefined)?.before);
                toast.error("Not removed", describeError(error));
            },
            onSettled: () => client.invalidateQueries({ queryKey: keys.envoy.saved }),
        },
        (variables) => variables.postingId,
    );

    const pending = useMutationState({
        filters: { mutationKey: ["envoy", "saved"], status: "pending" },
        select: (mutation) => (mutation.state.variables as { postingId: string }).postingId,
    });

    // A press on a posting while its save or unsave is on its way is
    // dropped: a double press on Save is a save, not a save and an unsave.
    // Read from the cache at the press, so it holds across every button.
    const moving = (postingId: string) =>
        [SAVE, UNSAVE].some(
            (mutationKey) =>
                client.getMutationCache().findAll({
                    mutationKey,
                    status: "pending",
                    predicate: (mutation) =>
                        (mutation.state.variables as { postingId?: string } | undefined)
                            ?.postingId === postingId,
                }).length > 0,
        );

    return {
        save: {
            ...save,
            mutate: ((variables, options) => {
                if (!moving(variables.postingId)) save.mutate(variables, options);
            }) as typeof save.mutate,
        },
        forget: {
            ...forget,
            mutate: ((variables, options) => {
                if (!moving(variables.postingId)) forget.mutate(variables, options);
            }) as typeof forget.mutate,
        },
        pending,
    };
}

/**
 * Marks a saved posting applied or skipped, or back to only saved, shown at
 * once and put back if the service refuses. The person's own tracking: it
 * is not an application and spends nothing.
 */
export function useMarkSaved() {
    const client = useQueryClient();
    const toast = useToast();
    return useSingleFlightMutation(
        {
            mutationKey: MARK,
            mutationFn: ({
                postingId,
                status,
                reason = null,
            }: {
                postingId: string;
                status: SavedStatus;
                reason?: SkipReason | null;
            }) => markSaved(postingId, status, reason),
            onMutate: ({ postingId, status, reason = null }) => ({
                before: changeSaved(client, (list) => ({
                    ...list,
                    saved: list.saved.map((entry) =>
                        entry.posting_id === postingId
                            ? {
                                  ...entry,
                                  status,
                                  status_at: status === "SAVED" ? null : new Date().toISOString(),
                                  skip_reason: status === "SKIPPED" ? reason : null,
                              }
                            : entry,
                    ),
                })),
            }),
            onError: (error, _variables, context) => {
                const before = (context as { before?: SavedPostingList } | undefined)?.before;
                client.setQueryData(keys.envoy.saved, before);
                toast.error("Not marked", describeError(error));
            },
            onSettled: () => client.invalidateQueries({ queryKey: keys.envoy.saved }),
        },
        (variables) => variables.postingId,
    );
}
